// components/lead-generation/business-types/ESGLeadCard.js
// FIXED: Proper rendering of recommendation objects

import React, { useState } from 'react';
import ESGLeadDetailsModal from './ESGLeadDetailsModal';

export default function ESGLeadCard({ lead, isSelected, onSelect }) {
  const [showDetails, setShowDetails] = useState(false);

  const getComplianceColor = (requirement) => {
    switch (requirement) {
      case 'CSRD_MANDATORY':
        return 'text-red-700 bg-red-100 border-red-200';
      case 'VSME_VOLUNTARY':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const getComplianceLabel = (requirement) => {
    switch (requirement) {
      case 'CSRD_MANDATORY':
        return 'CSRD Required';
      case 'VSME_VOLUNTARY':
        return 'VSME Eligible';
      default:
        return 'Future Prep';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'CRITICAL':
        return 'fas fa-exclamation-triangle text-red-500';
      case 'HIGH':
        return 'fas fa-arrow-up text-red-500';
      case 'MEDIUM':
        return 'fas fa-minus text-yellow-500';
      default:
        return 'fas fa-arrow-down text-gray-500';
    }
  };

  const getIndustryIcon = (industryRisk) => {
    switch (industryRisk?.level) {
      case 'HIGH':
        return 'fas fa-industry text-red-500';
      case 'MEDIUM':
        return 'fas fa-building text-yellow-500';
      default:
        return 'fas fa-briefcase text-gray-500';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `€${(amount / 1000).toFixed(0)}K`;
    }
    return `€${amount}`;
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    return deadline.includes('-') ? deadline.split('-')[0] : deadline;
  };

  // FIXED: Safe recommendation display function
  const getFirstRecommendationText = (recommendations) => {
    if (!recommendations || !Array.isArray(recommendations) || recommendations.length === 0) {
      return 'No specific recommendations available';
    }
    
    const firstRecommendation = recommendations[0];
    
    // Check if it's an object with a description
    if (typeof firstRecommendation === 'object' && firstRecommendation.description) {
      return firstRecommendation.description;
    }
    
    // Check if it's an object with a title
    if (typeof firstRecommendation === 'object' && firstRecommendation.title) {
      return firstRecommendation.title;
    }
    
    // If it's already a string, return it
    if (typeof firstRecommendation === 'string') {
      return firstRecommendation;
    }
    
    // Fallback
    return 'ESG compliance recommendation available';
  };

  return (
    <>
      <div className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer ${
        isSelected ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
      }`}>
        
        {/* Card Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(lead.id)}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 truncate">{lead.name}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <i className={getIndustryIcon(lead.esgAssessment?.industryRisk)} />
                  <span className="text-xs text-gray-500">
                    {lead.esgAssessment?.industryRisk?.category || lead.industry}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(lead.totalScore || 0)}`}>
                {lead.totalScore || 0}%
              </div>
            </div>
          </div>
        </div>

        {/* Card Body */}
        <div className="p-4" onClick={() => setShowDetails(true)}>
          <div className="space-y-3">
            
            {/* ESG Compliance Status */}
            <div className={`px-3 py-2 rounded-lg border ${getComplianceColor(lead.esgAssessment?.primaryRequirement)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <i className="fas fa-leaf text-sm" />
                  <span className="text-sm font-medium">
                    {getComplianceLabel(lead.esgAssessment?.primaryRequirement)}
                  </span>
                </div>
                {lead.esgAssessment?.csrd?.deadline && (
                  <span className="text-xs font-medium">
                    Due: {formatDeadline(lead.esgAssessment.csrd.deadline)}
                  </span>
                )}
              </div>
            </div>

            {/* Company Metrics */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center space-x-2 text-gray-600">
                <i className="fas fa-users text-gray-400" />
                <span>{lead.employees || 0} employees</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <i className="fas fa-chart-line text-gray-400" />
                <span>{formatCurrency(lead.turnover)}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <i className="fas fa-map-marker-alt text-gray-400" />
                <span className="truncate">{lead.location || lead.city || 'Unknown'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className={getPriorityIcon(lead.priority)} />
                <span className="text-sm font-medium">{lead.priority || 'LOW'}</span>
              </div>
            </div>

            {/* ESG Risk Indicator */}
            {lead.esgAssessment?.industryRisk && (
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">ESG Risk Level:</span>
                <span className={`text-sm font-medium ${
                  lead.esgAssessment.industryRisk.level === 'HIGH' 
                    ? 'text-red-600' 
                    : lead.esgAssessment.industryRisk.level === 'MEDIUM' 
                      ? 'text-yellow-600' 
                      : 'text-green-600'
                }`}>
                  {lead.esgAssessment.industryRisk.level}
                </span>
              </div>
            )}

            {/* Business Value Estimate */}
            {lead.esgAssessment?.businessValue && (
              <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                <span className="text-sm text-gray-600">Opportunity Value:</span>
                <span className="text-sm font-medium text-blue-700">
                  {formatCurrency(lead.esgAssessment.businessValue.estimated)}
                </span>
              </div>
            )}

            {/* FIXED: Key ESG Insight - Safe Recommendation Display */}
            {lead.esgAssessment?.recommendations && lead.esgAssessment.recommendations.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded p-2">
                <div className="flex items-start space-x-2">
                  <i className="fas fa-lightbulb text-green-600 text-sm mt-0.5" />
                  <p className="text-xs text-green-700 line-clamp-2">
                    {getFirstRecommendationText(lead.esgAssessment.recommendations)}
                  </p>
                </div>
              </div>
            )}
            
            {/* Alternative: Show reasoning if recommendations fail */}
            {(!lead.esgAssessment?.recommendations || lead.esgAssessment.recommendations.length === 0) && lead.reasoning && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <div className="flex items-start space-x-2">
                  <i className="fas fa-info-circle text-blue-600 text-sm mt-0.5" />
                  <p className="text-xs text-blue-700 line-clamp-2">
                    {Array.isArray(lead.reasoning) ? lead.reasoning.join('. ') : lead.reasoning}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-4 flex space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDetails(true);
              }}
              className="flex-1 bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
            >
              <i className="fas fa-chart-bar mr-2" />
              ESG Analysis
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Add to customers logic
                console.log('Adding lead to customers:', lead.name);
              }}
              className="bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
            >
              <i className="fas fa-plus text-xs" />
            </button>
          </div>
        </div>

        {/* Compliance Urgency Banner */}
        {lead.esgAssessment?.csrd?.urgency === 'IMMEDIATE' && (
          <div className="bg-red-500 text-white px-4 py-2 text-xs font-medium rounded-b-lg">
            <i className="fas fa-clock mr-2" />
            URGENT: CSRD compliance deadline approaching
          </div>
        )}
        
        {lead.esgAssessment?.csrd?.urgency === 'HIGH' && (
          <div className="bg-orange-500 text-white px-4 py-2 text-xs font-medium rounded-b-lg">
            <i className="fas fa-exclamation-triangle mr-2" />
            HIGH PRIORITY: CSRD implementation needed
          </div>
        )}
      </div>

      {/* ESG Lead Details Modal */}
      {showDetails && (
        <ESGLeadDetailsModal
          lead={lead}
          onClose={() => setShowDetails(false)}
        />
      )}
    </>
  );
}